import { useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash, UploadSimple, X } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiErrorDetail } from "@/lib/api";
import { siteImg } from "@/lib/siteContent";
import { EDGE_PROFILES, catalogBySlug } from "@/lib/edgeProfiles";

const seed = () =>
  EDGE_PROFILES.map((p, i) => ({
    slug: p.slug,
    base: p.slug,
    name: p.name,
    type: p.type,
    tag: p.tag || "",
    body: p.body || "",
    best: p.best || "",
    enabled: !p.optional,
    sort: i,
  }));

const uid = () => `custom-${Math.random().toString(36).slice(2, 8)}`;
const iconBtn = "flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-zinc-100";

const PhotoField = ({ value, onChange, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (f) => {
    if (!f) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post("/settings/site/image", form);
      onChange(data.path);
      toast.success("Edge photo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
    if (ref.current) ref.current.value = "";
  };
  return (
    <div className="flex items-center gap-2">
      <input ref={ref} type="file" accept="image/*" className="hidden" data-testid={`${testId}-input`} onChange={(e) => upload(e.target.files?.[0])} />
      {value && (
        <span className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900 p-1 pr-2">
          <img src={siteImg(value)} alt="" className="h-9 w-14 rounded object-cover" data-testid={`${testId}-thumb`} />
          <button type="button" data-testid={`${testId}-remove`} onClick={() => onChange("")} className="text-zinc-500 hover:text-red-300"><X size={13} weight="bold" /></button>
        </span>
      )}
      <Button type="button" size="sm" variant="outline" data-testid={`${testId}-upload`} disabled={busy} onClick={() => ref.current?.click()} className="h-9 border-zinc-700 text-[10px] font-bold uppercase tracking-widest">
        <UploadSimple size={13} weight="bold" className="mr-1.5" /> {busy ? "Uploading…" : value ? "Replace photo" : "Add cut-sample photo"}
      </Button>
    </div>
  );
};

const Preview = ({ base }) => {
  const p = catalogBySlug[base];
  if (!p) return null;
  const lam = p.type === "laminated";
  return (
    <svg
      viewBox={lam ? "-4 34 170 74" : "-4 34 170 44"}
      className="h-14 w-24 shrink-0 rounded bg-[#f4f1ea]"
      aria-hidden="true"
    >
      <path d={p.path} fill="#d9d0bf" stroke="#1c1b19" strokeWidth="2.5" strokeLinejoin="round" />
      {p.seam && <path d={p.seam} stroke="#b9a98f" strokeWidth="1" fill="none" />}
    </svg>
  );
};

// Office editor for the public Edges page catalogue (Settings → Website → Pages → Edges).
export const EdgeProfilesEditor = ({ value, onChange }) => {
  const list = value?.length ? value : seed();
  const update = (next) => onChange(next.map((e, i) => ({ ...e, sort: i })));
  const patch = (i, k, v) => update(list.map((e, idx) => (idx === i ? { ...e, [k]: v } : e)));
  const move = (i, dir) => {
    const next = [...list];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    update(next);
  };
  const remove = (i) => update(list.filter((_, idx) => idx !== i));
  const add = () => {
    const base = EDGE_PROFILES[0];
    update([
      ...list,
      { slug: uid(), base: base.slug, name: "New edge", type: base.type, tag: base.tag, body: base.body, best: base.best, enabled: true },
    ]);
  };
  const shown = list.filter((e) => e.enabled !== false).length;

  return (
    <div className="space-y-3" data-testid="site-edges-profiles">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          data-testid="site-edges-add"
          onClick={add}
          className="h-9 border border-zinc-800 bg-zinc-950 text-[10px] font-bold uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
        >
          <Plus size={13} weight="bold" className="mr-2" /> Add edge
        </Button>
        <Button
          type="button"
          variant="ghost"
          data-testid="site-edges-reset"
          onClick={() => onChange([])}
          className="h-9 text-[10px] font-bold uppercase tracking-widest text-zinc-500 hover:text-red-300"
        >
          Reset to defaults
        </Button>
        <span className="text-xs text-zinc-500" data-testid="site-edges-count">
          {shown} of {list.length} shown on the Edges page
        </span>
      </div>

      {list.map((e, i) => (
        <div key={e.slug || i} className="rounded-lg border border-zinc-800 bg-zinc-950/60 p-3" data-testid={`site-edge-${i}`}>
          <div className="flex items-start gap-3">
            <Preview base={e.base || e.slug} />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  data-testid={`site-edge-${i}-name`}
                  value={e.name || ""}
                  onChange={(ev) => patch(i, "name", ev.target.value)}
                  placeholder="Edge name"
                  className="h-9 border-zinc-800 bg-zinc-900 text-sm font-semibold"
                />
                <label className="flex shrink-0 items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500">
                  <Switch
                    data-testid={`site-edge-${i}-enabled`}
                    checked={e.enabled !== false}
                    onCheckedChange={(v) => patch(i, "enabled", v)}
                  />
                  Live
                </label>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <select
                  data-testid={`site-edge-${i}-type`}
                  value={e.type || "single"}
                  onChange={(ev) => patch(i, "type", ev.target.value)}
                  className="h-9 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200"
                >
                  <option value="single">Single · 2–3 cm</option>
                  <option value="laminated">Laminated · 6 cm</option>
                </select>
                <select
                  data-testid={`site-edge-${i}-base`}
                  value={e.base || e.slug}
                  onChange={(ev) => patch(i, "base", ev.target.value)}
                  title="Shape drawn on the site, 3D viewer and quotes"
                  className="h-9 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-xs text-zinc-200"
                >
                  {EDGE_PROFILES.map((s) => (
                    <option key={s.slug} value={s.slug}>
                      Shape: {s.name}
                    </option>
                  ))}
                </select>
              </div>
              <Input
                data-testid={`site-edge-${i}-tag`}
                value={e.tag || ""}
                onChange={(ev) => patch(i, "tag", ev.target.value)}
                placeholder="Short tagline"
                className="h-9 border-zinc-800 bg-zinc-950 text-xs"
              />
              <Textarea
                data-testid={`site-edge-${i}-body`}
                rows={2}
                value={e.body || ""}
                onChange={(ev) => patch(i, "body", ev.target.value)}
                placeholder="Description shown in the quick-view"
                className="border-zinc-800 bg-zinc-950 text-xs"
              />
              <PhotoField value={e.photo || ""} onChange={(v) => patch(i, "photo", v)} testId={`site-edge-${i}-photo`} />
            </div>
            <div className="flex shrink-0 flex-col gap-1">
              <button type="button" data-testid={`site-edge-${i}-up`} onClick={() => move(i, -1)} className={iconBtn}><ArrowUp size={12} weight="bold" /></button>
              <button type="button" data-testid={`site-edge-${i}-down`} onClick={() => move(i, 1)} className={iconBtn}><ArrowDown size={12} weight="bold" /></button>
              <button type="button" data-testid={`site-edge-${i}-remove`} onClick={() => remove(i)} className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-800 text-zinc-500 hover:text-red-300"><Trash size={12} weight="bold" /></button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
